import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Auto Glass Shop';
const BASE_URL = 'https://www.autoglassshop.com.au';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/AGS_logo.png`;

// Pathname of the initially served (possibly prerendered) HTML — used to know
// when the user has SPA-navigated away, so stale prerendered tags can be removed.
const INITIAL_PATHNAME = typeof window !== 'undefined' ? window.location.pathname : '';

/**
 * useSEO — sets document.title, meta description, canonical, and Open Graph tags.
 * Call once per page component.
 *
 * @param {object} options
 * @param {string} options.title        — page title (without site name suffix)
 * @param {string} options.description  — meta description (max 160 chars)
 * @param {string} [options.canonical]  — override canonical URL (default: current path)
 * @param {string} [options.ogImage]    — Open Graph image URL
 * @param {string} [options.ogType]     — 'website' | 'product' (default: 'website')
 * @param {string} [options.keywords]   — comma-separated keyword phrases for meta[name="keywords"]
 * @param {boolean} [options.noindex]   — if true, sets robots noindex,follow
 */
const useSEO = ({ title, description, canonical, ogImage, ogType = 'website', keywords, noindex = false }) => {
  const location = useLocation();

  useEffect(() => {
    // --- Title --- (skip suffix if the title already contains the site name,
    // so it stays identical to the server-prerendered title)
    document.title = title
      ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
      : SITE_NAME;

    // --- Clean up stale tags left over from the prerendered page or a
    // previously visited page during SPA navigation ---
    if (location.pathname !== INITIAL_PATHNAME) {
      document
        .querySelectorAll('script[type="application/ld+json"][data-prerender]')
        .forEach((s) => s.remove());
      document
        .querySelectorAll('link[rel="alternate"][media]')
        .forEach((l) => l.remove());
    }
    if (!keywords) {
      const staleKeywords = document.querySelector('meta[name="keywords"]');
      if (staleKeywords) staleKeywords.remove();
    }

    // --- Helper to upsert a <meta> tag ---
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = attr.split('=');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    // --- Helper to upsert a <link> tag ---
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    const canonicalUrl = canonical
      ? canonical
      : `${BASE_URL}${location.pathname}`;

    const desc = (description || '').substring(0, 160);
    const image = ogImage || DEFAULT_OG_IMAGE;

    // --- Robots ---
    const robotsContent = noindex ? 'noindex,follow' : 'index,follow';
    setMeta('meta[name="robots"]', 'name=robots', robotsContent);

    // --- Standard meta ---
    setMeta('meta[name="description"]', 'name=description', desc);

    // --- Keywords ---
    if (keywords) {
      setMeta('meta[name="keywords"]', 'name=keywords', keywords);
    }

    // --- Canonical ---
    setLink('canonical', canonicalUrl);

    // --- Open Graph ---
    setMeta('meta[property="og:title"]', 'property=og:title', document.title);
    setMeta('meta[property="og:description"]', 'property=og:description', desc);
    setMeta('meta[property="og:url"]', 'property=og:url', canonicalUrl);
    setMeta('meta[property="og:type"]', 'property=og:type', ogType);
    setMeta('meta[property="og:image"]', 'property=og:image', image);
    setMeta('meta[property="og:site_name"]', 'property=og:site_name', SITE_NAME);

    // --- Twitter Card ---
    setMeta('meta[name="twitter:card"]', 'name=twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name=twitter:title', document.title);
    setMeta('meta[name="twitter:description"]', 'name=twitter:description', desc);
    setMeta('meta[name="twitter:image"]', 'name=twitter:image', image);
  }, [title, description, canonical, ogImage, ogType, keywords, noindex, location.pathname]);
};

export default useSEO;
